'use client';

import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Card } from '@app/app/_components/Card/Card';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { CoverDetailsView } from '@app/app/esales/life/_sections/steps/CoverDetailsView';
import { LifeCover } from '@app/domain/model/eshop/life/LifeCover';
import { LifePackage } from '@app/domain/model/eshop/life/LifePackage';
import { determineLifePackageTitle } from '@app/infrastructure/eshop/transport/determineLifePackageTitle';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { useTranslations } from 'next-intl';
import React, { useCallback, useMemo, useState } from 'react';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    selectedPackage: Nullable<LifePackage>;
    open: boolean;
    onClose: () => void;
}

interface LifeCoverWithPackageSum extends LifeCover {
    packageSum: number;
    isAccident: boolean;
    packageDescription?: string;
}

export function PackageDetailsView({ selectedPackage, open, onClose }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();
    const [selectedCoverForDetails, setSelectedCoverForDetails] = useState<Nullable<LifeCoverWithPackageSum>>(null);
    const [isCoverDetailsOpen, setIsCoverDetailsOpen] = useState(false);

    const matchedCovers = useMemo(() => {
        if (!selectedPackage || selectedPackage.covers.length === 0) return [];

        return selectedPackage.covers
            .map(packageCover => {
                const fullCover = selectedPackage.covers.find(cover => cover.code === packageCover.code);
                if (!fullCover) return null;

                return {
                    ...fullCover,
                    packageSum: packageCover.default,
                    isAccident: packageCover.isAccident,
                    packageDescription: packageCover.description,
                };
            })
            .filter(Boolean);
    }, [selectedPackage]);

    const handleCoverReadMoreClick = useCallback(
        (coverCode: string) => {
            const coverItem = matchedCovers.find(cover => cover?.code === coverCode);
            if (coverItem) {
                setSelectedCoverForDetails(coverItem);
                setIsCoverDetailsOpen(true);
            }
        },
        [matchedCovers],
    );

    const handleCloseCoverDetails = useCallback(() => {
        setIsCoverDetailsOpen(false);
        setSelectedCoverForDetails(null);
    }, []);

    const handleCloseAll = useCallback(() => {
        onClose();
        setIsCoverDetailsOpen(false);
        setSelectedCoverForDetails(null);
    }, [onClose]);

    if (!open || !selectedPackage) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                className="fixed inset-0 z-30 bg-black opacity-45"
                onClick={onClose}
                aria-label={t('general.close')}
            />

            <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50 md:flex md:items-center md:justify-center md:p-8">
                <div className="flex h-full w-full flex-col bg-neutral-50 md:min-w-dialog md:max-w-dialog">
                    <div className="mb-4 flex items-start justify-between px-4 py-4">
                        <Heading level="h2" className="flex-1 pt-1">
                            {`${t(determineLifePackageTitle(selectedPackage.code))} ${t('eshop.life.package').toLowerCase()}`}
                        </Heading>
                        <GhostButton onClick={onClose} className="flex-shrink-0">
                            <CloseOutlinedCircleIcon className="size-8" />
                        </GhostButton>
                    </div>

                    <div className="flex-1 px-4 pb-6">
                        <div className="flex flex-col gap-4">
                            {matchedCovers.map((cover, index) => (
                                <React.Fragment key={`${cover?.code}-${index}`}>
                                    {cover && (
                                        <Card className="p-4">
                                            <Heading level="h3" className="mb-2">
                                                {t(determineCoverageName(cover.code))}
                                            </Heading>
                                            <Paragraph className="mb-2">
                                                <span dangerouslySetInnerHTML={{ __html: cover.shortDescription }} />
                                            </Paragraph>
                                            <Heading level="h1" className="font-light">
                                                {formatEuroValue(cover.packageSum, tenant, true)}
                                            </Heading>
                                            <CallbackButton
                                                className="mx-0 self-start p-0"
                                                variant="text"
                                                id={cover.code}
                                                onClick={handleCoverReadMoreClick}
                                                rightAddon={<ArrowRightIcon className="size-5" />}>
                                                {t('general.readMore')}
                                            </CallbackButton>
                                        </Card>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <CoverDetailsView
                selectedCover={selectedCoverForDetails}
                selectedCoverPackageSum={selectedCoverForDetails?.packageSum}
                open={
                    isCoverDetailsOpen &&
                    selectedCoverForDetails != null &&
                    (selectedCoverForDetails.compensation.length > 0 || selectedCoverForDetails.description != null)
                }
                onBack={handleCloseCoverDetails}
                onClose={handleCloseAll}
            />
        </>
    );
}
